/**
 * PlansSection — LIVE public pricing (R2, HeyCatch gap analysis §D.1).
 *
 *   1. loading  → skeleton cards (never a spinner-only blank).
 *   2. live     → the real, PUBLIC plan catalogue (`listPlans()` → GET
 *                 /api/v1/plans — unauthenticated by design): every active plan
 *                 incl. the Founder tier, priced in the selected currency
 *                 (CAD / USD / EUR via the shared currency selector), a
 *                 monthly/annual presentation toggle and the trial badge read
 *                 from GET /api/v1/plans/public-config (never hard-coded).
 *   3. fallback → the qualitative TEASER_PLANS ONLY when the catalogue is
 *                 unreachable (network failure / outage) — never a crash, never
 *                 a raw error, never a stale price; a retry is offered.
 *
 * A plan CTA starts Keycloak REGISTRATION with `returnTo =
 * /settings/subscription?plan=<code>&currency=<cur>`, so the new account lands
 * on the subscription page with that plan preselected.
 */

import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { CurrencySelector } from '@/components/CurrencySelector';
import { useT } from '@/i18n';
import { formatMoney } from '@/lib/money';
import {
  getPublicPricingConfig,
  listPlans,
  overagePriceFor,
  type Plan,
  type PublicPricingConfig,
} from '@/lib/api/subscription';
import { TEASER_PLANS, type TeaserPlan } from '@/routes/landing/landing-data';
import { useCurrency } from '@/stores/currency-store';
import { Icon, SectionHeading, useLandingCta } from './shared';

const SUBSCRIPTION_PATH = '/settings/subscription';

/** Limits surfaced on the public card (in this order) when the plan sets them. */
const CARD_LIMITS = ['contacts', 'seats', 'projects', 'ai_agents'] as const;
/** Capability flags surfaced on the card when the plan INCLUDES them. */
const CARD_FEATURES = ['ai_teams', 'advanced_analytics', 'content_studio', 'automations'] as const;

/** Static Tailwind classes (JIT needs literal class names). */
const XL_COLS: Record<number, string> = {
  1: 'xl:grid-cols-1',
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
  4: 'xl:grid-cols-4',
  5: 'xl:grid-cols-5',
};

type State =
  | { status: 'loading' }
  | { status: 'live'; plans: Plan[] }
  | { status: 'fallback' };

/** Return path for a plan CTA (exported for tests). */
export function planReturnTo(code: string, currency: string): string {
  return `${SUBSCRIPTION_PATH}?plan=${encodeURIComponent(code)}&currency=${encodeURIComponent(currency)}`;
}

/** The highlighted tier: Founder when offered, else the second card. */
export function highlightedCode(plans: Plan[]): string | null {
  if (plans.some((p) => p.code === 'founder')) return 'founder';
  if (plans.length >= 2) return plans[1].code;
  return plans[0]?.code ?? null;
}

export function PlansSection() {
  const { t, locale } = useT();
  const { signup } = useLandingCta();
  const { currency, setSupported } = useCurrency();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [config, setConfig] = useState<PublicPricingConfig | null>(null);
  const [annual, setAnnual] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setState((s) => (s.status === 'live' ? s : { status: 'loading' }));
    listPlans(currency)
      .then((plans) => {
        if (!alive) return;
        const active = plans
          .filter((p) => p.is_active)
          .sort((a, b) => a.sort_order - b.sort_order);
        const sup = plans.find((p) => p.supported_currencies?.length)?.supported_currencies;
        if (sup) setSupported(sup);
        setState(active.length > 0 ? { status: 'live', plans: active } : { status: 'fallback' });
      })
      .catch(() => {
        // The catalogue is PUBLIC: reaching this branch means the network or
        // the backend is down. Show the honest qualitative teaser + retry.
        if (alive) setState({ status: 'fallback' });
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, attempt, locale]); // refetch: plan copy is localized server-side

  useEffect(() => {
    let alive = true;
    getPublicPricingConfig()
      .then((c) => {
        if (alive) setConfig(c);
      })
      .catch(() => {
        // No badge rather than an unverified claim.
        if (alive) setConfig(null);
      });
    return () => {
      alive = false;
    };
  }, [attempt]);

  const choose = useCallback((code: string) => signup(planReturnTo(code, currency)), [signup, currency]);

  const trialDays = config && config.trial_on_signup && config.trial_days > 0 ? config.trial_days : 0;
  const highlight = state.status === 'live' ? highlightedCode(state.plans) : null;
  const liveCount = state.status === 'live' ? state.plans.length : 3;

  return (
    <section
      id="tarifs"
      data-testid="landing-plans"
      data-state={state.status}
      className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24"
    >
      <SectionHeading
        eyebrow={t('adoption.plans.eyebrow')}
        title={t('adoption.plans.title')}
        subtitle={t('adoption.plans.subtitle')}
      />

      {trialDays > 0 && (
        <div className="mt-5 flex justify-center">
          <span
            data-testid="trial-badge"
            className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-300"
          >
            <Icon name="check" className="h-4 w-4" strokeWidth={2.4} />
            {t('adoption.plans.trialBadge', { days: trialDays })}
          </span>
        </div>
      )}

      {state.status === 'live' && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
          <CurrencySelector label={t('adoption.plans.currency')} id="plans-currency" />
          <div
            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface p-1 shadow-elevation-1"
            role="group"
            aria-label={t('adoption.plans.billingFrequency')}
          >
            <ToggleBtn active={!annual} onClick={() => setAnnual(false)}>
              {t('adoption.plans.monthly')}
            </ToggleBtn>
            <ToggleBtn active={annual} onClick={() => setAnnual(true)}>
              {t('adoption.plans.annual')}
              <span className="ml-1.5 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                {t('adoption.plans.annualHint')}
              </span>
            </ToggleBtn>
          </div>
        </div>
      )}

      <div
        className={`mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 ${XL_COLS[Math.min(Math.max(liveCount, 1), 5)]}`}
      >
        {state.status === 'loading' && [0, 1, 2].map((i) => <PlanSkeleton key={i} />)}

        {state.status === 'live' &&
          state.plans.map((plan) => (
            <LivePlanCard
              key={plan.code}
              plan={plan}
              annual={annual}
              highlighted={plan.code === highlight}
              locale={locale}
              onChoose={() => choose(plan.code)}
            />
          ))}

        {state.status === 'fallback' &&
          TEASER_PLANS.map((plan) => (
            <TeaserPlanCard key={plan.code} plan={plan} onChoose={() => choose(plan.code)} />
          ))}
      </div>

      {state.status === 'fallback' && (
        <div className="mt-6 flex flex-col items-center gap-3 text-center" data-testid="plans-fallback">
          <p className="text-sm text-muted">{t('adoption.plans.fallbackNote')}</p>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className="rounded-token border border-border bg-surface px-4 py-2 text-sm font-semibold text-foreground shadow-elevation-1 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('adoption.plans.retry')}
          </button>
        </div>
      )}
    </section>
  );
}

function ToggleBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        active ? 'bg-primary text-primary-foreground shadow-elevation-1' : 'text-muted hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

function PlanFrame({
  highlighted,
  badge,
  testId,
  children,
}: {
  highlighted: boolean;
  badge: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div
      data-testid={testId}
      data-highlighted={highlighted ? 'true' : 'false'}
      className={`relative flex h-full flex-col rounded-token-xl border bg-surface p-6 shadow-elevation-1 transition duration-200 ease-out hover:-translate-y-1 hover:shadow-elevation-3 motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${
        highlighted ? 'border-primary/60 ring-2 ring-primary/30' : 'border-border'
      }`}
    >
      {highlighted && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-1 text-xs font-semibold text-white shadow-elevation-2">
          {badge}
        </span>
      )}
      {children}
    </div>
  );
}

function FeatureLi({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <Icon name="check" className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" strokeWidth={2.2} />
      <span className="text-sm leading-relaxed text-foreground">{children}</span>
    </li>
  );
}

function ChooseButton({
  highlighted,
  onChoose,
  label,
  testId,
}: {
  highlighted: boolean;
  onChoose: () => void;
  label: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onChoose}
      data-testid={testId}
      className={`mt-6 inline-flex w-full items-center justify-center rounded-token px-5 py-2.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg motion-reduce:transition-none ${
        highlighted
          ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-elevation-2 hover:brightness-110'
          : 'border border-border bg-surface text-foreground shadow-elevation-1 hover:bg-surface-2'
      }`}
    >
      {label}
    </button>
  );
}

const KNOWN_CODES = ['free', 'founder', 'starter', 'pro', 'enterprise'] as const;
type KnownCode = (typeof KNOWN_CODES)[number];
const isKnownCode = (c: string): c is KnownCode => (KNOWN_CODES as readonly string[]).includes(c);

function LivePlanCard({
  plan,
  annual,
  highlighted,
  locale,
  onChoose,
}: {
  plan: Plan;
  annual: boolean;
  highlighted: boolean;
  locale: string;
  onChoose: () => void;
}) {
  const { t } = useT();
  const numberLocale = locale === 'fr' ? 'fr-CA' : locale === 'ar' ? 'ar' : 'en-CA';
  const isFree = plan.price_monthly <= 0;
  // Annual view: 2 months offered → per-month equivalent + a "billed annually" note.
  const perMonth = annual ? (plan.price_monthly * 10) / 12 : plan.price_monthly;
  const annualTotal = plan.price_monthly * 10;
  const money = (n: number) => formatMoney(n, plan.currency || 'cad');
  const fmtLimit = (v: number) => (v < 0 ? t('adoption.plans.unlimited') : new Intl.NumberFormat(numberLocale).format(v));

  // French = the catalogue's own (admin-editable) description; other locales use
  // the translated tagline for the known tiers and fall back to the catalogue.
  const description =
    locale === 'fr' || !isKnownCode(plan.code) ? plan.description : t(`adoption.plans.descriptions.${plan.code}`);
  const limits = CARD_LIMITS.filter((k) => typeof plan.limits?.[k] === 'number');
  const features = CARD_FEATURES.filter((k) => plan.features?.[k] === true);
  const overage = overagePriceFor(plan, plan.currency);

  return (
    <PlanFrame
      highlighted={highlighted}
      badge={plan.code === 'founder' ? t('adoption.plans.founderBadge') : t('adoption.plans.popularBadge')}
      testId={`plan-card-${plan.code}`}
    >
      <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
      {description && <p className="mt-1 text-sm text-muted">{description}</p>}

      <div className="mt-5">
        {isFree ? (
          <p className="text-4xl font-extrabold tracking-tight text-foreground" data-testid={`plan-price-${plan.code}`}>
            {t('adoption.plans.free')}
          </p>
        ) : (
          <>
            <p className="flex items-baseline gap-1">
              <span
                className="text-3xl font-extrabold tracking-tight text-foreground"
                data-testid={`plan-price-${plan.code}`}
                data-currency={plan.currency}
                data-amount={plan.price_monthly}
              >
                {money(perMonth)}
              </span>
              <span className="text-sm font-medium text-muted">{t('adoption.plans.perMonth')}</span>
            </p>
            <p className="mt-1 text-xs text-muted">
              {annual
                ? t('adoption.plans.billedAnnually', { total: money(annualTotal) })
                : t('adoption.plans.billedMonthly')}
            </p>
            {plan.price_fallback && (
              <p className="mt-0.5 text-[11px] text-muted" title={t('adoption.plans.priceFallbackHint')}>
                {t('adoption.plans.priceFallback', { currency: plan.currency.toUpperCase() })}
              </p>
            )}
          </>
        )}
      </div>

      <ul className="mt-6 space-y-3">
        {plan.ai_credits_monthly > 0 && (
          <FeatureLi>
            {t('adoption.plans.credits', {
              count: new Intl.NumberFormat(numberLocale).format(plan.ai_credits_monthly),
            })}
          </FeatureLi>
        )}
        {limits.map((key) => (
          <FeatureLi key={key}>
            {t(`adoption.plans.limits.${key}`)} : {fmtLimit(plan.limits[key])}
          </FeatureLi>
        ))}
        {features.map((key) => (
          <FeatureLi key={key}>{t(`adoption.plans.features.${key}`)}</FeatureLi>
        ))}
        {overage > 0 && (
          <FeatureLi>
            {t('adoption.plans.overage', {
              price: formatMoney(overage, plan.currency, { maximumFractionDigits: 4 }),
            })}
          </FeatureLi>
        )}
      </ul>

      <div className="mt-auto">
        <ChooseButton
          highlighted={highlighted}
          onChoose={onChoose}
          label={isFree ? t('adoption.plans.ctaStartFree') : t('adoption.plans.ctaChoose', { plan: plan.name })}
          testId={`plan-cta-${plan.code}`}
        />
      </div>
    </PlanFrame>
  );
}

function TeaserPlanCard({ plan, onChoose }: { plan: TeaserPlan; onChoose: () => void }) {
  const { t } = useT();
  const name = t(plan.nameKey);
  return (
    <PlanFrame highlighted={plan.highlighted} badge={t('adoption.plans.founderBadge')} testId={`teaser-card-${plan.code}`}>
      <h3 className="text-lg font-bold text-foreground">{name}</h3>
      <p className="mt-1 text-sm text-muted">{t(plan.taglineKey)}</p>
      <div className="mt-5">
        <p className="text-4xl font-extrabold tracking-tight text-foreground">{t(plan.priceKey)}</p>
        <p className="mt-1 text-xs text-muted">{t('adoption.plans.teaser.period')}</p>
      </div>
      <ul className="mt-6 space-y-3">
        {plan.featureKeys.map((k) => (
          <FeatureLi key={k}>{t(k)}</FeatureLi>
        ))}
      </ul>
      <div className="mt-auto">
        <ChooseButton
          highlighted={plan.highlighted}
          onChoose={onChoose}
          label={t('adoption.plans.ctaChoose', { plan: name })}
          testId={`teaser-cta-${plan.code}`}
        />
      </div>
    </PlanFrame>
  );
}

function PlanSkeleton() {
  return (
    <div className="flex h-full animate-pulse flex-col rounded-token-xl border border-border bg-surface p-6 shadow-elevation-1 sm:p-8">
      <div className="h-5 w-24 rounded-full bg-surface-2" />
      <div className="mt-3 h-3 w-40 rounded-full bg-surface-2" />
      <div className="mt-6 h-10 w-32 rounded-token bg-surface-2" />
      <div className="mt-6 space-y-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-3 w-full rounded-full bg-surface-2" />
        ))}
      </div>
      <div className="mt-8 h-10 w-full rounded-token bg-surface-2" />
    </div>
  );
}
